import { Collapsible as CollapsiblePrimitive } from '@base-ui/react/collapsible';
import { useRender } from '@base-ui/react/use-render';
import { ChevronRightIcon } from 'lucide-react';
import type { ReactElement } from 'react';
import { IconButton } from '../candidates/icon-button.tsx';
import { cn } from '../util/classes.ts';
import { Container, type ContainerProps } from './container.tsx';

export type CollapsibleProps = ContainerProps & {
    open?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
};

/**
 * A collapsible page section: the whole heading row toggles the content below.
 * The page-section sibling of {@link Card} — same mechanism, but paints no
 * surface and doesn't deepen the container level.
 *
 * Style open-state-dependent content with the `collapsible-open:` variant.
 * Anything that appears/disappears should transition (like
 * {@link CollapsibleSubtitle}) — never `collapsible-open:hidden`, which snaps.
 */
export function Collapsible({
    className,
    level,
    minLevel = 1,
    nestLevel = 0,
    open,
    defaultOpen,
    onOpenChange,
    render,
    children,
    ...props
}: CollapsibleProps): ReactElement {
    return (
        <CollapsiblePrimitive.Root
            open={open}
            defaultOpen={defaultOpen}
            onOpenChange={onOpenChange}
            render={
                <Container
                    render={render}
                    className={cn(
                        'flex flex-col py-container',
                        'collapsible-interactive-child:relative collapsible-interactive-child:z-1',
                        className,
                    )}
                    level={level}
                    minLevel={minLevel}
                    nestLevel={nestLevel}
                    data-slot="collapsible"
                    {...props}
                />
            }
        >
            {children}
        </CollapsiblePrimitive.Root>
    );
}

export type CollapsibleHeaderProps = useRender.ComponentProps<'div'>;

/**
 * The always-visible row of a {@link Collapsible}. Holds the heading, an
 * optional action, and the expand affordance.
 */
export function CollapsibleHeader({ className, render, ...props }: CollapsibleHeaderProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        render,
        props: {
            ...props,
            className: cn(
                'group relative flex items-center gap-default',
                'has-data-[slot=collapsible-trigger]:cursor-pointer',
                className,
            ),
            'data-slot': 'collapsible-header',
        },
    });
}

export type CollapsibleHeadingProps = useRender.ComponentProps<'div'>;

/**
 * Stacks {@link CollapsibleTitle} above {@link CollapsibleSubtitle}.
 */
export function CollapsibleHeading({ className, render, ...props }: CollapsibleHeadingProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        render,
        props: {
            ...props,
            // No gap — the title↔subtitle spacing collapses with the subtitle row.
            className: cn('flex min-w-0 flex-col', className),
            'data-slot': 'collapsible-heading',
        },
    });
}

export type CollapsibleTitleProps = useRender.ComponentProps<'h3'>;

export function CollapsibleTitle({ className, render, ...props }: CollapsibleTitleProps): ReactElement {
    return useRender({
        defaultTagName: 'h3',
        render,
        props: {
            ...props,
            className: cn('flex items-center gap-tight text-ui-lg font-semibold text-content', className),
            'data-slot': 'collapsible-title',
        },
    });
}

export type CollapsibleSubtitleProps = useRender.ComponentProps<'div'>;

export function CollapsibleSubtitle({
    className,
    render,
    children,
    ...props
}: CollapsibleSubtitleProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        render,
        props: {
            ...props,
            // row collapses + fades in place; overflow stays visible (fade,
            // not crop), hence pointer-events off while faded
            className: cn(
                // surface motion, synced with the panel
                'grid grid-rows-[1fr] transition-[grid-template-rows,opacity] ease-standard',
                'duration-state collapsible-open:duration-interactive',
                'collapsible-open:grid-rows-[0fr] collapsible-open:opacity-0 collapsible-open:pointer-events-none',
                className,
            ),
            'data-slot': 'collapsible-subtitle',
            children: (
                <span className="flex min-h-0 gap-tight pt-tight text-ui-base text-content-weak">
                    {children}
                </span>
            ),
        },
    });
}

export type CollapsibleActionProps = useRender.ComponentProps<'div'>;

export function CollapsibleAction({ className, render, ...props }: CollapsibleActionProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        render,
        props: {
            ...props,
            // fades with open state; visibility keeps it out of tab order
            className: cn(
                'invisible flex items-center gap-cozy opacity-0',
                // surface motion, synced with the panel
                'transition-[opacity,visibility] ease-standard',
                'duration-state collapsible-open:duration-interactive',
                'collapsible-open:visible collapsible-open:opacity-100',
                className,
            ),
            'data-slot': 'collapsible-action',
        },
    });
}

export type CollapsibleTriggerProps = CollapsiblePrimitive.Trigger.Props;

export function CollapsibleTrigger({ ...props }: CollapsibleTriggerProps): ReactElement {
    return (
        <CollapsiblePrimitive.Trigger
            {...props}
            render={
                <IconButton
                    label="Toggle section"
                    data-slot="collapsible-trigger"
                    className={cn(
                        'ml-auto',
                        // row hover lights the chevron, unless the pointer
                        // is on the (non-toggling) action
                        'text-content-weak',
                        'group-hover:not-group-has-[[data-slot=collapsible-action]:hover]:text-content',
                        'group-hover:not-group-has-[[data-slot=collapsible-action]:hover]:bg-content/(--opacity-wash-hover)',
                        // the whole row is the click target
                        'after:absolute after:inset-0 active:translate-none',
                    )}
                >
                    <ChevronRightIcon className="transition-transform duration-state collapsible-open:rotate-90" />
                </IconButton>
            }
        />
    );
}

export type CollapsiblePanelProps = CollapsiblePrimitive.Panel.Props;

export function CollapsiblePanel({ className, children, ...props }: CollapsiblePanelProps): ReactElement {
    return (
        <CollapsiblePrimitive.Panel
            keepMounted
            data-slot="collapsible-panel"
            {...props}
            className={cn(
                // closed = display:none; data-starting/ending-style are the
                // visible frames that let the height transition run
                'h-(--collapsible-panel-height) overflow-hidden',
                // surface motion: in 320 / out 200 (Dialog recipe)
                'transition-[height,opacity] ease-standard duration-interactive',
                'data-ending-style:duration-state',
                'data-starting-style:h-0 data-starting-style:opacity-0',
                'data-ending-style:h-0 data-ending-style:opacity-0',
                className,
            )}
        >
            <div className="pt-container">{children}</div>
        </CollapsiblePrimitive.Panel>
    );
}
